import React, { ReactNode, useEffect, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import { usePopper } from 'react-popper';
import { RowTabStopContext, useRowTabIndex } from './row-tab-stop';

type Placement =
    | 'auto'
    | 'auto-start'
    | 'auto-end'
    | 'top'
    | 'top-start'
    | 'top-end'
    | 'bottom'
    | 'bottom-start'
    | 'bottom-end'
    | 'right'
    | 'right-start'
    | 'right-end'
    | 'left'
    | 'left-start'
    | 'left-end';

interface Props {
    children: ReactNode;
    icon?: ReactNode;
    closeOnMouseLeave?: boolean;
    backgroundColor?: string;
    className?: string;
    mirrorPanel?: boolean;
    placement?: Placement;
    onClickOpen?: () => void;
    /**
     * Names the trigger button for assistive tech. The trigger's only content
     * is usually an icon, and an icon is hidden from screen readers, so
     * without this it announces as an unnamed "button".
     *
     * Deliberately not defaulted: only the caller knows what the popover
     * holds. Omit it only when `icon` already carries text of its own,
     * visible or visually hidden.
     */
    triggerLabel?: string;
}

const TWPopover = ({
    children,
    icon,
    closeOnMouseLeave,
    backgroundColor = 'bg-gray-300',
    className,
    placement = 'bottom',
    onClickOpen,
    triggerLabel,
}: Props) => {
    const rowTabIndex = useRowTabIndex();
    const [referenceElement, setReferenceElement] = useState<HTMLElement>();
    const [popperElement, setPopperElement] = useState<HTMLElement>();
    const { styles, attributes } = usePopper(referenceElement, popperElement, {
        placement: placement,
        modifiers: [
            {
                name: 'flip',
                enabled: true,
                options: {
                    flipVariations: true,
                },
            },
        ],
    });

    // A panel that a list row opens does not take the row's Tab stop state.
    const panelContent = (
        <RowTabStopContext.Provider value={undefined}>
            {children}
        </RowTabStopContext.Provider>
    );

    return (
        <Popover className="relative">
            <PopoverButton
                ref={setReferenceElement}
                aria-label={triggerLabel}
                tabIndex={rowTabIndex}
                className={twMerge(
                    'hover:darken-10 focus-ring rounded-sm p-1',
                    backgroundColor,
                    className,
                )}
                onClick={onClickOpen}
            >
                {icon}
            </PopoverButton>
            <div className="relative h-full">
                <PopoverPanel
                    ref={setPopperElement}
                    style={styles.popper}
                    {...attributes.popper}
                    className="relative z-50"
                >
                    {({ close }) => (
                        <>
                            <MouseLeaveHandler
                                closeOnMouseLeave={closeOnMouseLeave}
                                close={close}
                            />
                            {panelContent}
                        </>
                    )}
                </PopoverPanel>
            </div>
        </Popover>
    );
};

export default TWPopover;

interface MouseLeaveHandlerProps {
    closeOnMouseLeave?: boolean;
    close: () => void;
}

const MouseLeaveHandler: React.FC<MouseLeaveHandlerProps> = ({
    closeOnMouseLeave,
    close,
}) => {
    useEffect(() => {
        if (closeOnMouseLeave === false) {
            close();
        }
    }, [closeOnMouseLeave, close]);

    return <div />;
};
